import { Link } from "react-router-dom";
import { DropdownMenu, Icon, Tooltip, Typography } from "@/components/atoms";
import { Vehicle } from "@/redux/slices/vehicle/types";
import { getAssetUrl } from "@/utils";
import { renderNumberPlate } from "../MyVehicles/helpers";
import { AffiliateVehicleTableData } from "./types";

export const generateAffiliateVehicleTableData = (
  operatorId: string,
  vehicles: Vehicle[],
  handleApprove: (vehicle: Vehicle, isReapprove?: boolean) => void,
  handleDeny: (vehicle: Vehicle) => void,
  onArchiveClick: (vehicle: Vehicle) => void,
  onSelectVehicleType?: (vehicle: Vehicle) => void,
  onAssignVehicleType?: (vehicle: Vehicle) => void
): AffiliateVehicleTableData[] => {
  const data = vehicles.map(
    (vehicle) =>
      ({
        numberPlate: renderNumberPlate(vehicle, operatorId, "affiliate-vehicles"),
        make: (
          <div className="flex items-center gap-2">
            <div className="flex h-6 w-6 items-center justify-center rounded bg-neutral-gray">
              <img
                src={getAssetUrl(vehicle.model.avatar, {
                  width: 36,
                  height: 36,
                  fit: "contain",
                  background: "transparent",
                })}
                alt={vehicle.model.make}
                className="h-[18px] w-[18px]"
              />
            </div>

            {vehicle.model.make}
          </div>
        ),
        model: vehicle.model.model,
        year: vehicle.year?.toString() ?? "-",
        color: (
          <div className="flex items-center gap-2 capitalize">
            {vehicle.color ? (
              <>
                {vehicle.color.toLowerCase().replace("_", " ")}
                {vehicle.color && (
                  <div
                    className="h-3 w-3 rounded-full border border-black"
                    style={{ backgroundColor: vehicle.color.toLowerCase().replace("_", "") }}
                  />
                )}
              </>
            ) : (
              "-"
            )}
          </div>
        ),
        state: (
          <div className="flex gap-2 ">
            {vehicle.state.image && <img src={vehicle.state.image} className="h-6 w-6 rounded-full object-cover" />}
            <Typography variant="action">{vehicle.state.abbreviation}</Typography>
          </div>
        ),
        owner: (
          <div className="flex items-center gap-2">
            {vehicle.owner ? (
              <>
                {vehicle.owner.avatar ? (
                  <img
                    src={getAssetUrl(vehicle.owner.avatar, {
                      width: 48,
                      height: 48,
                      fit: "contain",
                    })}
                    className="h-6 w-6 rounded-full"
                    alt={vehicle.owner?.name}
                  />
                ) : (
                  <Icon
                    name="Profile"
                    size="sm"
                    variant="Bold"
                    className="h-6 w-6 rounded-full bg-neutral-gray p-1 text-neutral-dark-gray"
                  />
                )}
                <Typography className="line-clamp-1">{vehicle.owner ? `${vehicle.owner.name}` : "-"}</Typography>
              </>
            ) : (
              <Typography className="line-clamp-1">-</Typography>
            )}
          </div>
        ),
        vehicleType: vehicle.vehicleType ? (
          <Typography>{vehicle.vehicleType.name}</Typography>
        ) : (
          <Tooltip content="No vehicle type assigned" placement="top">
            <Icon name="Danger" variant="Bold" size="sm" className="text-warning" />
          </Tooltip>
        ),
        vehicleCategory: vehicle.vehicleCategory?.name ?? "-",
        status: renderStatus(vehicle),
        actions: renderActions(vehicle, operatorId, handleApprove, handleDeny, onArchiveClick, onSelectVehicleType, onAssignVehicleType),
      } as AffiliateVehicleTableData)
  );

  return data;
};

const renderActions = (
  vehicle: Vehicle,
  operatorId: string,
  handleApprove: (vehicle: Vehicle, isReapprove?: boolean) => void,
  handleDeny: (vehicle: Vehicle) => void,
  onArchiveClick: (vehicle: Vehicle) => void,
  onSelectVehicleType?: (vehicle: Vehicle) => void,
  onAssignVehicleType?: (vehicle: Vehicle) => void
) => {
  const link = `/operator/${operatorId}/vehicles/affiliate-vehicles/${vehicle.uuid}`;
  const status = !vehicle.approval ? "PENDING" : vehicle.approval.status;
  const needsVehicleType = !vehicle.vehicleType;

  const handleApproveClick = () => {
    if (needsVehicleType && onSelectVehicleType) {
      onSelectVehicleType(vehicle);
    } else {
      handleApprove(vehicle);
    }
  };

  return (
    <>
      <DropdownMenu button={<Icon name="options" isCustom size="lg" />} position="bottom-start" className="!flex max-w-[24px]">
        <Link to={link}>
          <DropdownMenu.Item>
            <Icon name="ArrowRight" className="mr-2.5 text-primary-dark" size="sm" />
            <Typography className="text-primary-dark">View Vehicle</Typography>
          </DropdownMenu.Item>
        </Link>
        {status === "PENDING" && (
          <>
            <DropdownMenu.Item onClick={handleApproveClick}>
              <span className="mr-2.5 mt-0.5 h-2 w-2 shrink-0 rounded-full bg-success" />
              <div>
                <Typography className="text-primary-dark">Approve Vehicle</Typography>
                {needsVehicleType && (
                  <Typography variant="small" className="mt-0.5 text-neutral-dark-gray">
                    This vehicle has no vehicle type. You&apos;ll need to select one before approving.
                  </Typography>
                )}
              </div>
            </DropdownMenu.Item>
            <DropdownMenu.Item onClick={() => handleDeny(vehicle)}>
              <span className="mr-2.5 h-2 w-2 shrink-0 rounded-full bg-danger" />
              <Typography className="text-primary-dark">Deny Vehicle</Typography>
            </DropdownMenu.Item>
          </>
        )}
        {status === "APPROVED" && onAssignVehicleType && (
          <DropdownMenu.Item onClick={() => onAssignVehicleType(vehicle)}>
            <Icon name="Car" className="mr-2.5 text-primary-dark" size="sm" />
            <Typography className="text-primary-dark">{needsVehicleType ? "Set Vehicle Type" : "Change Vehicle Type"}</Typography>
          </DropdownMenu.Item>
        )}
        {status === "DENIED" && (
          <DropdownMenu.Item onClick={handleApproveClick}>
            <span className="mr-2.5 mt-0.5 h-2 w-2 shrink-0 rounded-full bg-success" />
            <div>
              <Typography className="text-primary-dark">Reapprove Vehicle</Typography>
              {needsVehicleType && (
                <Typography variant="small" className="mt-0.5 text-neutral-dark-gray">
                  This vehicle has no vehicle type. You&apos;ll need to select one before reapproving.
                </Typography>
              )}
            </div>
          </DropdownMenu.Item>
        )}
        <DropdownMenu.Item onClick={() => onArchiveClick(vehicle)}>
          <Icon name="Trash" className="mr-2.5 text-danger" size="sm" />
          <Typography className="text-primary-dark">Archive Vehicle</Typography>
        </DropdownMenu.Item>
      </DropdownMenu>
    </>
  );
};

const renderStatus = (vehicle: Vehicle) => {
  const status = !vehicle.approval ? "PENDING" : vehicle.approval.status;

  switch (status) {
    case "PENDING":
      return (
        <Typography variant="paragraph" className="flex flex-row items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-neutral-dark-gray" /> Pending
        </Typography>
      );
    case "APPROVED":
      return (
        <Typography variant="paragraph" className="flex flex-row items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-info" /> Approved
        </Typography>
      );
    case "DENIED":
      return (
        <Typography variant="paragraph" className="flex flex-row items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-danger" /> Denied
        </Typography>
      );
    default:
      return null;
  }
};
